import React from 'react'

import { v4 as uuid } from 'uuid'

import ButtonConfigurationScreen, {
  ButtonConfigurationOptions,
} from '../button-configuration-screen/ButtonConfigurationScreen'
import {
  asSkinProductOptionWithGridOrNull,
  ProductSeries,
  SkinProductOption,
  SkinProductOptionWithGrid,
} from '../types/productTypes'
import { SelectorScreen } from '../selector-screen/SelectorScreen'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { AppStateActions, WiserOrMomentary } from '../state/AppState'
import Button from '../components/Button'
import { inflateConfiguredInterface, resolveInterfaces } from '../products/productUtils'
import { ProductDataContext } from '../ProductDataContext'
import { IconCross } from '../components/icons'
import { ConfiguredProduct } from '../list/ListTypes'
import { validateMechs } from '../products/productValidation'
import { SelectorScreenMenuType } from '../selector-screen/SelectorScreenMenuType'
import {
  ConfiguratorScreenMode,
  ConfiguratorScreenReducer,
  getReducerStateInitializer,
  ReducerActionType,
} from './ConfiguratorScreenReducer'
import { UserType } from '../types/userTypes'
import SelectSeriesPopup from '../selector-screen/SelectSeriesPopup'
import { DeepLinkData } from './resolveDeeplinkProduct'
import { ENABLE_ELECTRICIAN_VIEW } from '../environment'
import productRequiresWiserHub from '../products/productRequiresWiserHub'
import { WiserMomentarySwitchesPromptPopup } from '../wiser/WiserMomentarySwitchesPromptPopup'
import { WiserHubPromptPopup } from '../wiser/WiserHubPromptPopup'
import { DefaultSwitchSelectionProducts } from '../TopLevelSelectionProducts'
import { WiserSwitchesProductItemNo } from '../data/wiserSwitchesProduct'
import { AnalyticsContext } from '../analytics/AnalyticsContext'
import { getAnalyticsProductInfo, getEcommercePayloadFrom } from '../analytics/AnalyticsHelpers'

interface Props {
  editingProduct: Readonly<ConfiguredProduct> | null
  deepLinkData?: DeepLinkData | null
  isInEditMode: boolean
  shouldDisableUserTypeSelectionWhenEditingButtons: boolean
  onAddProduct: (product: ConfiguredProduct) => void
  onCancel?: () => void
  onSetIsUserTypeSelectorEnabled: (value: boolean) => void
  isEntryFromDeepLink: boolean
}

const ConfiguratorScreen: React.FunctionComponent<Props> = ({
  isEntryFromDeepLink,
  editingProduct,
  isInEditMode,
  onAddProduct,
  onCancel,
  onSetIsUserTypeSelectorEnabled,
  deepLinkData,
  shouldDisableUserTypeSelectionWhenEditingButtons,
}) => {
  const Products = React.useContext(ProductDataContext)
  const context = React.useContext(ConfiguratorAppContext)
  const analyticsLogger = React.useContext(AnalyticsContext)

  const [reducerState, dispatch] = React.useReducer(
    ConfiguratorScreenReducer,
    null,
    getReducerStateInitializer(
      Products,
      editingProduct,
      deepLinkData ?? null,
      isInEditMode,
      context.selectedProductSeries ?? ProductSeries.Iconic,
    )
  )

  const { dispatch: appDispatch } = context

  const { expandedMenu, selectedProductData, ledEnabled, screen, configuredButtonIds } = reducerState
  const { skinItemNo: selectedProductId, orientation: selectedOrientation } = selectedProductData

  React.useEffect(() => {
    if (expandedMenu === 'TopLevel') {
      appDispatch({ action: AppStateActions.WiserModeChanged, newMode: null })
    }
  }, [appDispatch, expandedMenu])

  const uid = editingProduct?.uid ?? null

  const allProducts = Products.skinGridProducts

  const onEnterButtonConfiguration = () => dispatch({ action: ReducerActionType.CONFIGURE_BUTTONS_CLICKED })

  const onExitButtonConfiguration = (config: ButtonConfigurationOptions) => dispatch({
    action: ReducerActionType.FINISHED_BUTTON_CONFIGURATION,
    configuredButtonIds: config.buttons,
    ledEnabled: config.ledEnabled,
    products: Products,
  })

  const selectedProduct = React.useMemo(() => {
    return allProducts.find(it => {
      return it.itemNo === selectedProductId
        && it.orientation === selectedOrientation
    }) ?? allProducts[0]
  }, [allProducts, selectedProductId, selectedOrientation])

  const configurableProduct: SkinProductOptionWithGrid | null = asSkinProductOptionWithGridOrNull(selectedProduct)

  const setSelectedProduct = (product: SkinProductOption) => {
    if (selectedProductId !== product.itemNo || selectedOrientation !== product.orientation) {
      dispatch({
        action: ReducerActionType.PRODUCT_SELECTED,
        product: {
          skinItemNo: product.itemNo,
          orientation: product.orientation,
        },
        products: Products,
      })
    }
  }

  const [showingWiserPrompt, setShowingWiserPrompt] = React.useState(false)
  const [showingMomentarySwitchesPrompt, setShowingMomentarySwitchesPrompt] = React.useState(false)

  const resolvedInterfaces = resolveInterfaces(selectedProduct, configuredButtonIds, Products)

  const validationMessages = validateMechs(Products, resolvedInterfaces.map(it => inflateConfiguredInterface(it, Products)), selectedProduct.type, selectedProduct.grid)
  const isSelectionValid = validationMessages.length === 0
  const interfaceWarningIndices: readonly number[] = validationMessages.map(it => it.interfaceIndex).filter((it): it is number => it !== null)

  const shouldShowMomentarySwitchesPrompt = ( () => {
    if (context.hasOptedOutOfMomentarySwitchesPrompt) {
      return false
    }

    const momentarySwitchesRuleForItem = Products.rules.find(rule => {
      return rule.ruleType === 'RequiresMomentarySwitches'
         && rule.products.includes(selectedProduct.itemNo)
    })

    return momentarySwitchesRuleForItem !== undefined
  })()

  const onClickAddProduct = () => {
    if (
      !context.hasOptedOutOfWiserHubPrompt
      && (
        productRequiresWiserHub(selectedProduct.itemNo, Products.rules)
        || productRequiresWiserHub(selectedProduct.grid, Products.rules)
      )
    ) {
      setShowingWiserPrompt(true)
    } else if (shouldShowMomentarySwitchesPrompt) {
      //Momentary Switches prompt will show after Wiser Hub prompt for products that need the WH
      //If product does not need WH, show now instead
      setShowingMomentarySwitchesPrompt(true)
    }

    analyticsLogger.log({
      event: 'add_to_cart',
      distributor: null,
      add_to_cart_location: 'Product selector',
      cta_name: 'Done',
      ecommerce: getEcommercePayloadFrom(
        [{
          product: getAnalyticsProductInfo(selectedProduct),
          quantity: 1,
          price: null
        }],
        {
          name: context.listName,
          id: context.listId
        }
      )
    })

    onAddProduct({
      uid: uid ?? uuid(),
      skinItemNo: selectedProduct.itemNo,
      interfaces: resolvedInterfaces,
      ledEnabled,
      orientation: selectedOrientation,
    })
  }

  const onWiserPromptAction = (params: { shouldAddWiserHub: boolean, doNotAskAgain: boolean, ctaName: string }) => {
    setShowingWiserPrompt(false)

    if (params.doNotAskAgain) {
      appDispatch({ action: AppStateActions.DisableWiserHubPrompt })
    }

    if (params.shouldAddWiserHub) {
      addWiserHub(params.ctaName)
    }

    if (shouldShowMomentarySwitchesPrompt) {
      setShowingMomentarySwitchesPrompt(true)
    }
  }

  const addWiserHub = (ctaName: string) => {
    const configuredHub: ConfiguredProduct = {
      uid: uuid(),
      skinItemNo: Products.constants.WiserHub,
      interfaces: [],
      ledEnabled: false,
      orientation: 'Horizontal',
    }

    analyticsLogger.log({
      event: 'add_to_cart',
      distributor: null,
      add_to_cart_location: 'Product prompt',
      cta_name: ctaName,
      ecommerce: getEcommercePayloadFrom(
        [{
          product: {
            name: 'Wiser Hub',
            secondaryName: null,
            itemId: Products.constants.WiserHub,
            category: 'Wiser',
            variant: null
          },
          quantity: 1,
          price: null
        }],
        {
          name: context.listName,
          id: context.listId
        }
      )
    })

    onAddProduct(configuredHub)
  }

  const onMomentarySwitchesPromptAction = (params: { shouldConfigureMomentarySwitches: boolean, doNotAskAgain: boolean }) => {
    setShowingMomentarySwitchesPrompt(false)

    if (params.doNotAskAgain) {
      appDispatch({ action: AppStateActions.DisableMomentarySwitchesPrompt })
    }

    if (params.shouldConfigureMomentarySwitches) {
      onClickAddMomentarySwitches()
    }
  }

  const setExpandedMenu = (menu: SelectorScreenMenuType) => dispatch({ action: ReducerActionType.MENU_SELECTED, menu })

  const [shouldShowSelectorScreenEntryAnimations, setShouldShowSelectorScreenEntryAnimations] = React.useState(true)

  const isConsumer = context.userType === UserType.Consumer

  const selectDefaultSwitch = () => {
    setSelectedProduct(
      allProducts.find((skinProduct) => DefaultSwitchSelectionProducts.includes(skinProduct.itemNo))
      ?? (() => { throw Error('no default switch found') })()
    )

    setExpandedMenu(SelectorScreenMenuType.Style)
  }

  const onClickAddMomentarySwitches = () => {
    let mechOverride: string | null = null

    const matchingRule = Products.rules.find(rule => {
      return rule.products?.includes(selectedProductId)
        && rule.ruleType === 'RequiresMomentarySwitches'
    })

    if (matchingRule?.ruleType === 'RequiresMomentarySwitches') {
      mechOverride = matchingRule.overrideMech
    }

    appDispatch({
      action: AppStateActions.WiserModeChanged,
      newMode: { type: WiserOrMomentary.MomentarySwitches, mechOverride: mechOverride }
    })

    selectDefaultSwitch()
  }

  const onClickCustomiseWiserSwitches = () => {
    appDispatch({
      action: AppStateActions.WiserModeChanged,
      newMode: { type: WiserOrMomentary.Wiser, mechOverride: '' }
    })

    selectDefaultSwitch()
  }

  // reset the view if the user changes to consumer, Pro series will be hidden
  React.useEffect(() => {
    if (isConsumer) {
      dispatch({
        action: ReducerActionType.CHANGE_SERIES,
        series: ProductSeries.Iconic,
        products: Products,
      })
    }
  }, [Products, isConsumer])


  // Hide User Type Selection button when ButtonConfigurator is open.
  React.useEffect(() => {
    if (!shouldDisableUserTypeSelectionWhenEditingButtons) {
      return
    }

    if (screen === ConfiguratorScreenMode.ButtonConfiguration) {
      onSetIsUserTypeSelectorEnabled(false)
    } else {
      onSetIsUserTypeSelectorEnabled(true)
    }
    // onSetIsUserTypeSelectorEnabled is stable so should never change.
  }, [screen, onSetIsUserTypeSelectorEnabled, shouldDisableUserTypeSelectionWhenEditingButtons])

  const isCustomiseScreen = screen === ConfiguratorScreenMode.ButtonConfiguration

  // set series preference (for electricians) whenever a new product is selected
  // ignore if editing a product already in the list
  // only switch series if:
  //   -  electrician has already selected a series before (they need to make an explicit choice initially)
  //   -  or if on the customise screen, e.g. deep links
  React.useEffect(() => {
    if (isInEditMode) {
      return
    }

    if (context.selectedProductSeries || isCustomiseScreen) {
      appDispatch({
        action: AppStateActions.ProductSeriesChanged,
        series: selectedProduct.pro ? ProductSeries.Pro : ProductSeries.Iconic,
      })
    }
  }, [appDispatch, selectedProduct.pro, isInEditMode, context.selectedProductSeries, isCustomiseScreen])

  const handleChooseSeries = (series: ProductSeries) => {
    appDispatch({
      action: AppStateActions.ProductSeriesChanged,
      series: series === ProductSeries.Pro ? ProductSeries.Pro : ProductSeries.Iconic
    })

    dispatch({
      action: ReducerActionType.CHANGE_SERIES,
      series: series,
      products: Products,
    })
  }

  return (
    <>
      {!context.selectedProductSeries && context.userType === UserType.Electrician && ENABLE_ELECTRICIAN_VIEW && context.proSeriesEnabled && (
        <SelectSeriesPopup
          handleChooseSeries={series => {
            analyticsLogger.log({
              event: 'cta_click',
              cta_name: series.toString(),
              cta_link: null,
              cta_category: 'Button',
              cta_location: 'Select range dialog'
            })

            handleChooseSeries(series)
          }}
        />
      )}
      {screen === ConfiguratorScreenMode.ProductSelector && isInEditMode && (
        <div className="editingHeader">
          <Button
            round
            buttonStyle="redStroke"
            onClick={() => {
              context.dispatch({ action: AppStateActions.CancelEditing })
            }}
          >
            <img src={IconCross} alt="Cancel" />
          </Button>
          <Button
            buttonStyle="green"
            onClick={onClickAddProduct}
            disabled={!isSelectionValid}
          >
            Done
          </Button>
        </div>
      )}

      {screen === ConfiguratorScreenMode.ProductSelector && (
        <SelectorScreen
          isEntryFromDeepLink={isEntryFromDeepLink}
          interfaceWarningIndices={interfaceWarningIndices}
          canAddToList={isSelectionValid}
          isInEditMode={isInEditMode}
          selectedProduct={selectedProduct}
          allProducts={allProducts}
          setSelectedProduct={setSelectedProduct}
          configuredButtonIds={configuredButtonIds}
          onClickCustomise={() => {
            if (!!configurableProduct) {
              onEnterButtonConfiguration()
              setShouldShowSelectorScreenEntryAnimations(false)
            } else if (selectedProduct.itemNo === WiserSwitchesProductItemNo) {
              onClickCustomiseWiserSwitches()
            }
          }}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
          shouldShowEntryAnimations={shouldShowSelectorScreenEntryAnimations}
          onClickSave={onClickAddProduct}
          onChangeSeries={series => {
            // Update local configurator state
            dispatch({
              action: ReducerActionType.CHANGE_SERIES,
              series,
              products: Products,
            })

            // Update app-level state (will sync with URL)
            appDispatch({
              action: AppStateActions.ProductSeriesChanged,
              series,
            })
          }}
        />
      )}

      {screen === ConfiguratorScreenMode.ButtonConfiguration && !!configurableProduct && (
        <ButtonConfigurationScreen
          isInEditMode={isInEditMode}
          product={configurableProduct}
          initialButtonSelection={configuredButtonIds}
          initialLedEnabled={ledEnabled}
          onBack={config => {
            if (isInEditMode && onCancel) {
              onCancel()
              return
            }

            onExitButtonConfiguration(config)
          }}
          onDone={config => {
            onExitButtonConfiguration(config)
            analyticsLogger.log({
              event: 'add_to_cart',
              distributor: null,
              add_to_cart_location: 'Product selector',
              cta_name: 'Done',
              ecommerce: getEcommercePayloadFrom(
                [{
                  product: getAnalyticsProductInfo(selectedProduct),
                  quantity: 1,
                  price: null
                }],
                {
                  name: context.listName,
                  id: context.listId
                }
              )
            })

            onAddProduct({
              uid: uid ?? uuid(),
              skinItemNo: selectedProduct.itemNo,
              interfaces: resolveInterfaces(selectedProduct, config.buttons, Products),
              ledEnabled: config.ledEnabled,
              orientation: selectedOrientation,
            })
          }}
          onChangeSkinOption={skinItemNo => {
            const newSkin = allProducts.find(skin => skin.itemNo === skinItemNo)
            if (newSkin) {
              setSelectedProduct(newSkin)
            }
          }}
          shouldShowPackQuantity={context.shouldShowPackQuantityInCustomise}
          addWiserHubToList={addWiserHub}
        />
      )}
      {
        showingWiserPrompt &&
        <WiserHubPromptPopup onAction={onWiserPromptAction} />
      }
      {
        showingMomentarySwitchesPrompt &&
        <WiserMomentarySwitchesPromptPopup onAction={onMomentarySwitchesPromptAction} />
      }
    </>
  )
}

export default ConfiguratorScreen
